import { type ReactNode } from 'react'
import {
  groupOptionsBySubCategory,
  type SubCategoryGroup,
  type SubCategoryGroupOption
} from '../Dropdown/DropdownListSubCategory/utils'

export interface SubCategoryGroupWithEmptyContent<T> extends SubCategoryGroup<T> {
  emptyContent?: ReactNode
}

/**
 * Builds subcategory groups from submenu options, then inserts empty groups for labels
 * present in `subCategoryEmptyContent` that have no options.
 *
 * Empty groups not listed in the map are omitted. Map key order is preserved for
 * listed labels; other option-backed groups follow in their natural order.
 *
 * Callers must pass the empty map for a single parent submenu (the dropdown looks this
 * up by parent `value` from its nested `subCategoryEmptyContent` prop). Intended for
 * parent `subOptions` submenus — not the root dropdown list.
 */
export const buildSubCategoryGroupsWithEmptyContent = <T extends SubCategoryGroupOption>(
  options: T[],
  subCategoryEmptyContent?: Record<string, ReactNode>,
  optionsWithSubCategoryOnly = false
): Array<SubCategoryGroupWithEmptyContent<T>> | undefined => {
  const groups = groupOptionsBySubCategory(options, optionsWithSubCategoryOnly)
  const emptyMap = subCategoryEmptyContent ?? {}
  const emptyKeys = Object.keys(emptyMap)

  if ((groups == null || groups.length === 0) && emptyKeys.length === 0) {
    return undefined
  }

  const groupsByLabel = new Map((groups ?? []).map((group) => [group.label, group]))
  const result: Array<SubCategoryGroupWithEmptyContent<T>> = []
  const seen = new Set<string>()

  for (const label of emptyKeys) {
    const existing = groupsByLabel.get(label)
    const groupOptions = existing?.options ?? []
    result.push({
      label,
      avatar: existing?.avatar,
      options: groupOptions,
      ...(groupOptions.length === 0 ? { emptyContent: emptyMap[label] } : {})
    })
    seen.add(label)
  }

  for (const group of groups ?? []) {
    if (seen.has(group.label)) {
      continue
    }
    result.push(group)
  }

  return result.length > 0 ? result : undefined
}
